// v49.45 — MobileFabSheet.
// v50.236 — Lazy navigation pass 1. Partner "Find work" used to dump
// users at /partners (the COORD-facing partner directory, which is
// wrong) and offer no further help. It now resolves the top open
// requirement matching the partner's declared trades and deep-links
// straight into it via /requirements?req={id}. Falls back to the
// list when no profile / no trades / no matches. Same approach the
// MatchingOpportunitiesCard + MyCompanyTopMatchesCard surfaces use.
//
// Bottom sheet opened from the FAB (centre button) on MobileBottomNav.
// Surfaces role-aware quick actions — the "what do I do right now"
// short list that the v48 design used to short-circuit deep nav.

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useShellUser } from "@/features/auth/AuthContext";
import { useEffectiveRole } from "../roleOverride";
import { LABOURCORE_API } from "@/lib/api";
import type { Requirement } from "@/lib/api/types";

interface Action {
  id:     string;
  label:  string;
  hint:   string;
  /* Tone tints the leading icon chip. */
  tone:   "blue" | "green" | "amber" | "violet";
  /* v50.236 — async-aware so a "lazy" action can resolve the best
     destination before navigating (e.g. partner "Find work" looking
     up the top-matched open requirement). Async actions own their
     own fallback; they must always call navigate() exactly once. */
  go:     (navigate: (path: string) => void) => void | Promise<void>;
}

async function findTopMatchedRequirement(): Promise<Requirement | null> {
  try {
    const profile = await LABOURCORE_API.onboarding.getMyCapabilityProfile();
    const trades = profile?.trades_supplied || [];
    if (trades.length === 0) return null;
    const lists = await Promise.all(
      trades.map(t => LABOURCORE_API.requirements.listOpen({ trade: t })),
    );
    const seen = new Set<string>();
    const merged: Requirement[] = [];
    for (const list of lists) {
      for (const r of ((list as Requirement[] | null) || [])) {
        if (seen.has(r.id)) continue;
        seen.add(r.id);
        merged.push(r);
      }
    }
    merged.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return merged[0] || null;
  } catch {
    return null;
  }
}

const COORD_ACTIONS: Action[] = [
  { id: "req",     label: "Post a requirement", hint: "Brief out a new crew + trade for an upcoming project.", tone: "blue",
    go: (n) => n("/requirements") },
  { id: "find",    label: "Find suppliers",     hint: "Search the partner directory or open the local-supplier map.", tone: "green",
    go: (n) => n("/partners") },
  { id: "project", label: "Add a project",      hint: "Track a new fit-out with its trades, dates, and headcount.", tone: "amber",
    go: (n) => n("/projects") },
  { id: "rfq",     label: "Build a contract",   hint: "Open the contract offer wizard against any partner.", tone: "violet",
    go: (n) => n("/partners") },
];

const PARTNER_ACTIONS: Action[] = [
  { id: "work",    label: "Find work",          hint: "Jumps straight to your top-matched live requirement.", tone: "blue",
    /* v50.236 — Lazy: resolve the top trade-matching open requirement
       and deep-link into it. Falls back to /requirements when the
       partner hasn't declared trades / nothing matches. Pre-v50.236
       this incorrectly pointed to /partners (the coord-side supplier
       directory). */
    go: async (n) => {
      const top = await findTopMatchedRequirement();
      n(top ? `/requirements?req=${top.id}` : "/requirements");
    } },
  { id: "crew",    label: "Add an operative",   hint: "Put a new tradesperson on your crew with rates + certs.", tone: "green",
    go: (n) => n("/workers") },
  { id: "cert",    label: "Upload compliance",  hint: "Add a CSCS card, RTW, 18th Edition, insurance, etc.", tone: "amber",
    go: (n) => n("/compliance") },
  { id: "invoice", label: "Track earnings",     hint: "See invoiced, paid, outstanding and overdue across deals.", tone: "violet",
    go: (n) => n("/earnings") },
];

interface Props { open: boolean; onClose: () => void }

export function MobileFabSheet({ open, onClose }: Props) {
  const navigate = useNavigate();
  const user     = useShellUser();
  const role     = useEffectiveRole();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !user) return null;
  const actions = role === "partner" ? PARTNER_ACTIONS : COORD_ACTIONS;

  const toneTint = (tone: Action["tone"]) => {
    switch (tone) {
      case "green":  return { bg: "var(--color-lc-green-bg)", fg: "var(--color-lc-green)", border: "#BBF7D0" };
      case "amber":  return { bg: "var(--color-lc-amber-bg)", fg: "var(--color-lc-amber)", border: "#FDE68A" };
      case "violet": return { bg: "#F5F3FF", fg: "#7C3AED", border: "#DDD6FE" };
      default:       return { bg: "var(--color-lc-blue-light)", fg: "var(--color-lc-blue-dark)", border: "var(--color-lc-blue-border)" };
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end"
      style={{ background: "rgba(8,17,33,.55)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
      onClick={onClose}
      /* v50.71 — iOS Safari often drops the synthesised `click` event
         on a tap if the finger drifts >10px between touchstart and
         touchend (treated as a scroll attempt), which left this sheet
         feeling impossible to dismiss. onPointerDown fires
         immediately on touch/mouse-down regardless. Target guard
         keeps clicks inside the panel from closing — the inner panel
         already stopPropagation()s on click, but pointerdown bubbles
         independently. */
      onPointerDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="lc-safe-pb w-full bg-white rounded-t-2xl shadow-2xl flex flex-col max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Quick actions"
      >
        <div className="pt-2 pb-1 flex justify-center">
          <div className="w-10 h-1 rounded-full bg-lc-line" />
        </div>
        <div className="px-5 py-2.5 border-b border-lc-line">
          <div className="text-base font-extrabold text-lc-ink">Quick actions</div>
          <div className="text-[12px] text-lc-mute mt-0.5">What do you want to do next?</div>
        </div>
        <div className="overflow-y-auto px-3 py-2">
          <ul className="flex flex-col gap-1.5">
            {actions.map(a => {
              const t = toneTint(a.tone);
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => { a.go(navigate); onClose(); }}
                    className="w-full text-left px-3 py-3 rounded-lc-md flex items-center gap-3 hover:bg-lc-bg-2 active:bg-lc-blue-light/40 transition-colors"
                    style={{ WebkitTapHighlightColor: "transparent" }}
                  >
                    <span
                      aria-hidden="true"
                      className="w-10 h-10 rounded-lc-md flex items-center justify-center border shrink-0"
                      style={{ background: t.bg, color: t.fg, borderColor: t.border }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] font-extrabold text-lc-ink">{a.label}</span>
                      <span className="block text-[12px] text-lc-mute leading-snug">{a.hint}</span>
                    </span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-lc-faint shrink-0" aria-hidden="true">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
